import {
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http'
import { Injectable, makeStateKey, TransferState } from '@angular/core'
import { NavigationEnd, Router } from '@angular/router'
import { Observable, of } from 'rxjs'
import { filter, tap } from 'rxjs/operators'

type ResponseType = HttpRequest<unknown>['responseType']

interface TransferHttpResponse {
  body: any
  headers: Record<string, string[]>
  status?: number
  statusText?: string
  url?: string
  responseType?: ResponseType
}

@Injectable()
export class TransferHttpCacheInterceptor implements HttpInterceptor {
  private isCacheActive = true

  constructor(
    private router: Router,
    private transferState: TransferState,
  ) {
    // Stop using the cache if the application has stabilized, indicating initial rendering is
    // complete.
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => {
        this.isCacheActive = false
      })
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const key = request.headers.get('x-key')

    if (!this.isCacheActive || !key) {
      // Cache is no longer active. Pass the request through.
      return next.handle(request)
    }

    const storeKey = makeStateKey<TransferHttpResponse>(key)
    const response = this.transferState.get(storeKey, null)

    if (response) {
      // Request found in cache. Respond using it.
      let body: ArrayBuffer | Blob | string | undefined = response.body

      switch (response.responseType) {
        case 'arraybuffer':
          {
            // If we're in Node...
            if (typeof Buffer !== 'undefined') {
              const buf = Buffer.from(response.body)
              body = new ArrayBuffer(buf.length)
              const view = new Uint8Array(body)
              for (let i = 0; i < buf.length; ++i) {
                view[i] = buf[i]
              }
            } else if (typeof TextEncoder !== 'undefined') {
              // Modern browsers implement TextEncode.
              body = new TextEncoder().encode(response.body).buffer
            }
          }
          break
        case 'blob':
          body = new Blob([response.body])
          break
      }

      return of(
        new HttpResponse<any>({
          body,
          headers: new HttpHeaders(response.headers),
          status: response.status,
          statusText: response.statusText,
          url: response.url,
        }),
      )
    }

    // Request not found in cache. Make the request and cache it.
    const httpEvent = next.handle(request)

    return httpEvent.pipe(
      tap((event: HttpEvent<unknown>) => {
        if (event instanceof HttpResponse) {
          this.transferState.set<TransferHttpResponse>(storeKey, {
            body: event.body,
            headers: this.getHeaders(event.headers),
            status: event.status,
            statusText: event.statusText,
            url: event.url ?? '',
            responseType: request.responseType,
          })
        }
      }),
    )
  }

  private getHeaders(headers: HttpHeaders): Record<string, string[]> {
    const headersMap: Record<string, string[]> = {}

    for (const key of headers.keys()) {
      const value = headers.getAll(key)
      if (typeof value === 'string') {
        headersMap[key] = value
      }
    }

    return headersMap
  }
}
